import { ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Divider,
	Link,
	TextField,
	Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../../../hooks/useStores.tsx";
import BriaButton from "../../common/BriaButton/BriaButton.tsx";
import BriaTooltip from "../../common/BriaTooltip/BriaTooltip.tsx";
import ConfirmationPopup from "../../common/ConfirmationPopup/ConfirmationPopup.tsx";
import styles from "./TGEvalContactUsPopup.module.scss";

interface ContactUsPopupProps {
	open: boolean;
	onClose: () => void;
}

const TGEvalContactUsPopup = ({ open, onClose }: ContactUsPopupProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation.config.contact" });
	const { t: tThankYou } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation.config.thankYou" });

	const [description, setDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [openThankYou, setOpenThankYou] = useState(false);
	const { tgStore, appStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const handleRequestHelp = async () => {
		if (loading || description?.replace(/\s/g, "")?.length < 10) return;

		setLoading(true);
		try {
			const data = {
				projectId: String(tgProjectStore.projectToView.id),
				projectName: tgProjectStore.projectToView.project_name,
				TGEvalSupportText: description,
			};
			const response = await appStore.sendTGEvalSupport(data);
			if (response) {
				setDescription("");
				onClose();
				setOpenThankYou(true);
			}
		} catch (error) {
			console.error("Failed to send support request:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setDescription("");
		onClose();
	};
	return (
		<>
			<ConfirmationPopup
				open={open}
				onClose={handleCancel}
				hideCancel={true}
				hideActions={true}
				maxWidth="600px"
				paperClassName={styles.contactUsPopup}
				contentClassName={styles.contentClassName}
			>
				<Box className={styles.content}>
					<Typography className={styles.title}>{t("title")}</Typography>
					<Typography className={styles.description}>{t("description")}</Typography>

					<Box className={styles.section}>
						<Typography
							className={styles.sectionTitle}
							dangerouslySetInnerHTML={{ __html: t("fillInDetails") }}
						/>
						<TextField
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder={t("placeholder")}
							fullWidth
							multiline
							minRows={3}
							className={styles.textField}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
								}
							}}
						/>
					</Box>

					<Divider className={styles.divider} />

					<>
						<Accordion
							elevation={0}
							disableGutters
							className={styles.imageReferenceAccordion}
							expanded={isOpen}
							onChange={() => setIsOpen((prev) => !prev)}
						>
							<AccordionSummary className={styles.AccordionSummary} expandIcon={<ExpandMore />}>
								<Typography className={styles.quickFixesTitle}>{t("quickFixesTitle")}</Typography>
							</AccordionSummary>

							<AccordionDetails className={styles.details}>
								<>
									<Typography className={styles.quickFixesSubtitle}>
										{t("quickFixesSubtitle")}
									</Typography>

									<Box className={styles.tip}>
										<Typography className={styles.tipNumber}>1.</Typography>
										<Typography className={styles.tipText}>{t("tip1")}</Typography>
									</Box>
									<Typography
										className={styles.tip1Description}
										dangerouslySetInnerHTML={{ __html: t("tipDescription") }}
									/>

									<Box className={styles.tip}>
										<Typography className={styles.tipNumber}>2.</Typography>
										<Typography className={styles.tipText}>{t("tip2")}</Typography>
									</Box>
									<BriaTooltip className={styles.toolTip} open title={t("comingSoon")}>
										<Box className={styles.helpCenterLink}>
											<Link
												href=""
												rel="noopener noreferrer"
												className={styles.link}
												dangerouslySetInnerHTML={{ __html: t("helpCenterLink") }}
											></Link>
										</Box>
									</BriaTooltip>
								</>
							</AccordionDetails>
						</Accordion>
					</>
					<Divider className={styles.divider2} />

					<Box className={styles.actions}>
						<BriaButton onClick={handleCancel} buttonType="textMedium" className={styles.cancelButton}>
							{t("cancel")}
						</BriaButton>
						<BriaButton
							onClick={handleRequestHelp}
							buttonType="primaryMedium"
							loading={loading}
							disabled={loading || description?.replace(/\s/g, "")?.length < 10}
							className={styles.requestButton}
						>
							{t("requestHelp")}
						</BriaButton>
					</Box>
				</Box>
			</ConfirmationPopup>

			<ConfirmationPopup
				open={openThankYou}
				onClose={() => {
					setOpenThankYou(false);
				}}
				hideCancel={true}
				confirmButtonText={tThankYou("backToPlayground")}
				onClick={() => {
					setOpenThankYou(false);
				}}
				maxWidth="500px"
				paperClassName={styles.thankYouPopup}
				title={tThankYou("title")}
				description={tThankYou("description")}
			></ConfirmationPopup>
		</>
	);
};

export default observer(TGEvalContactUsPopup);
